import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Badge, Row, Col, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import Pager from './Pager';
import { formatDay } from '../utils/dates';

const PAGE_SIZE = 15;
const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const INVOICE_STATUS = { draft: 'borrador', ready: 'lista, sin enviar', pending_dgii: 'esperando a DGII' };

// Conduces → "Sin facturar" (backend services/unbilledConduces.js): delivered conduces whose pedido has no factura.
// "Facturar" opens the builder on the conduce's pedido (it starts from the conduce's lines). A factura that probably
// belongs to the conduce — same client, made around then, on a pedido with no conduce — is shown so it can be checked,
// and "Unir" moves it onto the conduce's pedido once a person confirms it is the conduce's bill (POST
// /conduces/:id/merge-factura). `rows` / `error` come from ConducesPage, which loads them once for the tab's badge too;
// `onChanged` reloads them.
const UnbilledConducesPanel = ({ rows, error, onChanged }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [client, setClient] = useState('');
  const [page, setPage] = useState(1);
  const [merging, setMerging] = useState('');
  const [notice, setNotice] = useState(null); // { ok, text }

  const merge = async (r, f) => {
    if (!window.confirm(`¿Unir la factura ${f.number} (${money(f.total)}) al conduce ${r.number}?\n\nEl conduce quedará como facturado con esa factura.`)) return;
    setMerging(`${r._id}:${f._id}`);
    try {
      const { data } = await API.post(`/conduces/${r._id}/merge-factura`, { invoiceId: f._id });
      setNotice({ ok: true, text: data.message });
      await onChanged?.();
    } catch (err) {
      setNotice({ ok: false, text: err.response?.data?.message || 'No se pudo unir la factura al conduce.' });
    } finally {
      setMerging('');
    }
  };

  useEffect(() => { setPage(1); }, [search, client]);

  // Per client, most tanks first — the clients that are never billed (own company, settled between companies) stand out.
  const byClient = useMemo(() => {
    const map = new Map();
    for (const r of rows || []) {
      const key = String(r.client?._id || '');
      const entry = map.get(key) || { id: key, name: r.client?.name || '—', conduces: 0, tanks: 0 };
      entry.conduces += 1; entry.tanks += r.tanks;
      map.set(key, entry);
    }
    return [...map.values()].sort((a, b) => b.tanks - a.tanks || b.conduces - a.conduces);
  }, [rows]);

  const term = norm(search.trim());
  const filtered = useMemo(() => (rows || []).filter(r => (!client || String(r.client?._id) === client)
    && (!term || [r.number, r.client?.name, r.client?.rnc, r.reference, r.pedido?.number].some(v => norm(v).includes(term)))), [rows, client, term]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const totalTanks = (rows || []).reduce((s, r) => s + r.tanks, 0);
  const withPossible = (rows || []).filter(r => r.possible.length > 0).length;

  if (error) return <div className="alert alert-danger">{error}</div>;
  if (rows === null) return <div className="text-muted py-3">Cargando...</div>;

  return (
    <>
      <div className="text-muted small mb-3">
        Conduces entregados cuyo pedido no tiene factura: tanques que salieron y no se han facturado. Algunos no se facturan
        nunca (por ejemplo, ventas entre empresas); otros se facturaron en una factura hecha sin elegir el pedido del conduce —
        para esos se muestra la factura que probablemente le corresponde y, si lo es, «Unir» la pasa al pedido del conduce. Los que paga un acuerdo con el cliente (pestaña
        Acuerdos) no aparecen. Solo conduces desde que cada venta tiene pedido.
      </div>
      <Row className="mb-3">
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Conduces sin facturar</div>
            <h2 className="mt-2 mb-0" style={{ color: rows.length ? 'var(--mm-yellow)' : 'var(--mm-green)' }}>{rows.length}</h2>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Tanques en esos conduces</div>
            <h2 className="mt-2 mb-0">{totalTanks.toLocaleString('es-DO')}</h2>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm p-3 dashboard-card h-100">
            <div className="text-muted small">Con una factura que probablemente les corresponde</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{withPossible}</h2>
          </Card>
        </Col>
      </Row>

      {notice && (
        <Alert variant={notice.ok ? 'success' : 'danger'}>{notice.text}</Alert>
      )}
      <Card className="shadow-sm p-3">
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Form.Control style={{ maxWidth: 320 }} placeholder="Buscar por número, cliente, referencia o pedido..." value={search} onChange={e => setSearch(e.target.value)} />
          <Form.Select style={{ maxWidth: 320 }} value={client} onChange={e => setClient(e.target.value)}>
            <option value="">Todos los clientes ({byClient.length})</option>
            {byClient.map(c => <option key={c.id} value={c.id}>{c.name} — {c.conduces} conduce{c.conduces === 1 ? '' : 's'}, {c.tanks} tanques</option>)}
          </Form.Select>
        </div>
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle">
            <thead>
              <tr><th>Conduce</th><th>Cliente</th><th>Entregado</th><th>Contenido</th><th>¿Ya facturado?</th><th /></tr>
            </thead>
            <tbody>
              {slice.map(r => (
                <tr key={r._id}>
                  <td>
                    <strong>{r.number}</strong>
                    {r.pedido && <div className="small text-muted">{r.pedido.number}</div>}
                    {r.reference && <div className="small text-muted">Ref. {r.reference}</div>}
                    {r.deal && <div className="small" style={{ color: 'var(--mm-cyan)' }}>Acuerdo cubre {money(r.deal.covered)} de {money(r.deal.value)}</div>}
                  </td>
                  <td>{r.client?.name || '—'}</td>
                  <td className="small">{formatDay(r.deliveredAt || r.createdAt)}</td>
                  <td className="small">{r.items.map((it, i) => <div key={i}>{it.quantity} × {it.description}</div>)}</td>
                  <td className="small">
                    {r.onTheWay && (
                      <div><Badge bg="info">En proceso</Badge> {r.onTheWay.number} ({INVOICE_STATUS[r.onTheWay.status] || r.onTheWay.status})</div>
                    )}
                    {!r.onTheWay && r.possible.map(f => (
                      <div key={f._id}>
                        <span style={{ color: 'var(--mm-cyan)' }}>Posible: </span>
                        <a href={`/invoices/${f._id}/print`} target="_blank" rel="noreferrer">{f.number}</a>
                        {' '}{formatDay(f.createdAt)} · {money(f.total)}
                        {f.sameProducts ? '' : <span className="text-muted"> (otros productos)</span>}
                        {r.pedido && (
                          <Button size="sm" variant="outline-info" className="ms-2 py-0 px-2" disabled={!!merging}
                            title={`Unir la factura ${f.number} a este conduce`} onClick={() => merge(r, f)}>
                            <i className="bi bi-link-45deg" /> {merging === `${r._id}:${f._id}` ? 'Uniendo…' : 'Unir'}
                          </Button>
                        )}
                      </div>
                    ))}
                    {!r.onTheWay && r.possible.length === 0 && <span className="text-muted">No</span>}
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {r.pedido && !r.onTheWay && (
                        <Button size="sm" variant="outline-primary" onClick={() => navigate(`/invoices/new?pedido=${r.pedido._id}`)}><i className="bi bi-receipt" /> Facturar</Button>
                      )}
                      <Button size="sm" variant="outline-secondary" onClick={() => window.open(`/conduces/${r._id}/print`, '_blank')}><i className="bi bi-printer" /> Ver</Button>
                    </div>
                  </td>
                </tr>
              ))}
              {slice.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">{rows.length ? 'Ningún conduce coincide.' : 'Todos los conduces entregados están facturados. 🎉'}</td></tr>
              )}
            </tbody>
          </Table>
        </div>
        {pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <span className="text-muted small">Página {current} de {pages} — {filtered.length} conduce{filtered.length === 1 ? '' : 's'}</span>
            <Pager page={current} pages={pages} onChange={setPage} />
          </div>
        )}
      </Card>
    </>
  );
};

export default UnbilledConducesPanel;
